import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { doc, getDoc, setDoc } from "firebase/firestore";
import Alert from "react-bootstrap/esm/Alert";
import Button from "react-bootstrap/esm/Button";
import Card from "react-bootstrap/esm/Card";
import Col from "react-bootstrap/esm/Col";
import Form from "react-bootstrap/esm/Form";
import Row from "react-bootstrap/esm/Row";
import Spinner from "react-bootstrap/esm/Spinner";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../services/firebase";
import { Globals } from "../globals";
import NotificationCredentials, { notificationCredentialsConverter } from "../DTOs/NotificationCredentials";
import I_IMC_Props from "../infos/I_IMC_Props";

/**
 * Superadmin only: the central channel credentials shared by every Gemeinde
 * (config/notificationCredentials – SMTP login, WhatsApp Cloud API, template).
 * Which Gemeinde may use a channel is set in the Gemeinden tab; whether a
 * Gemeinde actually uses it is set by its own admin.
 */
export default function NotificationCredentialsForm({ setVariant, setMessage, setShowMessage }: I_IMC_Props) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [migrating, setMigrating] = useState(false);
    const [credentials, setCredentials] = useState<NotificationCredentials>(new NotificationCredentials());

    const credentialsDoc = () =>
        doc(db, Globals.NOTIFICATION_CONFIG_COLLECTION_KEY, Globals.NOTIFICATION_CREDENTIALS_DOC_ID).withConverter(
            notificationCredentialsConverter
        );

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const snap = await getDoc(credentialsDoc());
                if (!cancelled && snap.exists()) setCredentials(snap.data());
            } catch {
                if (!cancelled) {
                    setVariant("danger");
                    setMessage(t("errors.loadingData"));
                    setShowMessage(true);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [setVariant, setMessage, setShowMessage, t]);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            await setDoc(credentialsDoc(), credentials, { merge: true });
            // The secrets go through a callable that encrypts them; an empty
            // field means "keep the stored one".
            const secrets: { emailSmtpPassword?: string; whatsappAccessToken?: string } = {};
            if (credentials.emailSmtpPassword) secrets.emailSmtpPassword = credentials.emailSmtpPassword;
            if (credentials.whatsappAccessToken) secrets.whatsappAccessToken = credentials.whatsappAccessToken;
            if (Object.keys(secrets).length > 0) {
                await httpsCallable(functions, "saveNotificationSecrets")(secrets);
                setCredentials({
                    ...credentials,
                    emailSmtpPassword: "",
                    whatsappAccessToken: "",
                    hasEmailSmtpPassword: credentials.hasEmailSmtpPassword || !!secrets.emailSmtpPassword,
                    hasWhatsappAccessToken: credentials.hasWhatsappAccessToken || !!secrets.whatsappAccessToken,
                });
            }
            setVariant("success");
            setMessage(t("notificationSettings.saved"));
        } catch (err: any) {
            setVariant("danger");
            setMessage(err?.message || t("errors.anErrorOccurred"));
        } finally {
            setShowMessage(true);
            setSaving(false);
        }
    };

    // Sends with the SAVED credentials, and only to the superadmin's own address.
    const handleTestEmail = async () => {
        setTesting(true);
        try {
            const sendTest = httpsCallable<Record<string, never>, { success: boolean; recipient: string }>(
                functions,
                "sendTestEmailNotification"
            );
            const result = await sendTest({});
            setVariant("success");
            setMessage(t("superadmin.testEmailSent", { recipient: result.data.recipient }));
        } catch (err: any) {
            setVariant("danger");
            setMessage(err?.message || t("errors.anErrorOccurred"));
        } finally {
            setShowMessage(true);
            setTesting(false);
        }
    };

    // Moves secrets that are still stored in plain text into encrypted storage.
    const handleMigrate = async () => {
        setMigrating(true);
        try {
            const migrate = httpsCallable<Record<string, never>, { central: number; gemeinden: number }>(
                functions,
                "migrateSecrets"
            );
            const result = await migrate({});
            setCredentials({ ...credentials, hasPlaintextSecrets: false });
            setVariant("success");
            setMessage(t("superadmin.secretsMigrated", result.data));
        } catch (err: any) {
            setVariant("danger");
            setMessage(err?.message || t("errors.anErrorOccurred"));
        } finally {
            setShowMessage(true);
            setMigrating(false);
        }
    };

    const set = (fields: Partial<NotificationCredentials>) => setCredentials({ ...credentials, ...fields });

    if (loading) return <Spinner animation="border" />;

    return (
        <Form onSubmit={handleSave}>
            <Alert variant="info">{t("superadmin.credentialsHint")}</Alert>
            {credentials.hasPlaintextSecrets && (
                <Alert variant="warning">
                    <p className="mb-2">{t("superadmin.secretsPlaintextWarning")}</p>
                    <Button variant="warning" size="sm" disabled={migrating} onClick={handleMigrate}>
                        {migrating && <Spinner as="span" animation="border" size="sm" className="me-2" />}
                        {t("superadmin.secretsMigrate")}
                    </Button>
                </Alert>
            )}

            <Card className="mb-3">
                <Card.Header>{t("notificationSettings.emailSection")}</Card.Header>
                <Card.Body>
                    <Row className="mb-3">
                        <Col md={8}>
                            <Form.Group>
                                <Form.Label>{t("notificationSettings.smtpHost")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={credentials.emailSmtpHost}
                                    onChange={(e) => set({ emailSmtpHost: e.target.value })}
                                    placeholder="smtp.gmail.com"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>{t("notificationSettings.smtpPort")}</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={credentials.emailSmtpPort}
                                    onChange={(e) => set({ emailSmtpPort: parseInt(e.target.value) || 587 })}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group className="mb-3">
                        <Form.Label>{t("notificationSettings.smtpUser")}</Form.Label>
                        <Form.Control
                            type="text"
                            value={credentials.emailSmtpUser}
                            onChange={(e) => set({ emailSmtpUser: e.target.value })}
                            placeholder="notifications@gmail.com"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t("notificationSettings.smtpPassword")}</Form.Label>
                        <Form.Control
                            type="password"
                            value={credentials.emailSmtpPassword}
                            onChange={(e) => set({ emailSmtpPassword: e.target.value })}
                            placeholder={
                                (credentials.hasEmailSmtpPassword
                                    ? t("superadmin.secretSetPlaceholder")
                                    : t("notificationSettings.smtpPasswordPlaceholder")) as string
                            }
                            autoComplete="new-password"
                        />
                        <Form.Text className="text-muted">
                            {t("notificationSettings.smtpPasswordHelp")} {t("superadmin.secretWriteOnly")}
                        </Form.Text>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>{t("notificationSettings.emailFrom")}</Form.Label>
                        <Form.Control
                            type="text"
                            value={credentials.emailFrom}
                            onChange={(e) => set({ emailFrom: e.target.value })}
                            placeholder="Serva <notifications@gmail.com>"
                        />
                    </Form.Group>
                    <div className="mt-3">
                        <Button variant="outline-success" size="sm" disabled={testing || saving} onClick={handleTestEmail}>
                            {testing && <Spinner as="span" animation="border" size="sm" className="me-2" />}
                            {t("superadmin.testEmail")}
                        </Button>
                        <Form.Text className="text-muted d-block">{t("superadmin.testEmailHelp")}</Form.Text>
                    </div>
                </Card.Body>
            </Card>

            <Card className="mb-3">
                <Card.Header>{t("notificationSettings.whatsappSection")}</Card.Header>
                <Card.Body>
                    <Alert variant="info" className="mb-3">
                        <p className="mb-1">
                            <strong>{t("notificationSettings.metaSetupTitle")}</strong>
                        </p>
                        <p className="mb-0" style={{ whiteSpace: "pre-line" }}>
                            {t("notificationSettings.metaSetupInfo")}
                        </p>
                    </Alert>
                    <Form.Group className="mb-3">
                        <Form.Label>{t("notificationSettings.metaAccessToken")}</Form.Label>
                        <Form.Control
                            type="password"
                            value={credentials.whatsappAccessToken}
                            onChange={(e) => set({ whatsappAccessToken: e.target.value })}
                            placeholder={
                                credentials.hasWhatsappAccessToken
                                    ? (t("superadmin.secretSetPlaceholder") as string)
                                    : "EAAG..."
                            }
                            autoComplete="new-password"
                        />
                        <Form.Text className="text-muted">
                            {t("notificationSettings.metaAccessTokenHelp")} {t("superadmin.secretWriteOnly")}
                        </Form.Text>
                    </Form.Group>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>{t("notificationSettings.metaPhoneNumberId")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={credentials.whatsappPhoneNumberId}
                                    onChange={(e) => set({ whatsappPhoneNumberId: e.target.value })}
                                    placeholder="123456789012345"
                                />
                                <Form.Text className="text-muted">
                                    {t("notificationSettings.metaPhoneNumberIdHelp")}
                                </Form.Text>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>{t("notificationSettings.metaBusinessAccountId")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={credentials.whatsappBusinessAccountId}
                                    onChange={(e) => set({ whatsappBusinessAccountId: e.target.value })}
                                    placeholder={t("notificationSettings.metaBusinessAccountIdPlaceholder") as string}
                                />
                                <Form.Text className="text-muted">
                                    {t("notificationSettings.metaBusinessAccountIdHelp")}
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>{t("notificationSettings.metaChoirTemplateName")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={credentials.whatsappChoirTemplateName}
                                    onChange={(e) => set({ whatsappChoirTemplateName: e.target.value })}
                                    placeholder="serva_chor_erinnerung"
                                />
                                <Form.Text className="text-muted">
                                    {t("notificationSettings.metaChoirTemplateNameHelp")}
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>{t("notificationSettings.metaTemplateName")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={credentials.whatsappTemplateName}
                                    onChange={(e) => set({ whatsappTemplateName: e.target.value })}
                                    placeholder="sermon_reminder"
                                />
                                <Form.Text className="text-muted">
                                    {t("notificationSettings.metaTemplateNameHelp")}
                                </Form.Text>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>{t("notificationSettings.metaTemplateLanguage")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={credentials.whatsappTemplateLanguage}
                                    onChange={(e) => set({ whatsappTemplateLanguage: e.target.value })}
                                    placeholder="de"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>{t("notificationSettings.metaApiVersion")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={credentials.whatsappApiVersion}
                                    onChange={(e) => set({ whatsappApiVersion: e.target.value })}
                                    placeholder="v21.0"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Button type="submit" variant="primary" disabled={saving}>
                {saving ? (
                    <>
                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                        {t("common.loading")}
                    </>
                ) : (
                    t("admin.save")
                )}
            </Button>
        </Form>
    );
}
